import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import data from '../data/paomakeup';
import { createLead } from '../services/leadService';
import { getErrorMessage } from '../utils/errorHandler';

const ContactUs = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertVariant, setAlertVariant] = useState('success');
    const [showAlert, setShowAlert] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm();

    const registerOptions = {
        name: {
            required: 'El nombre es requerido',
            minLength: { value: 3, message: 'El nombre debe tener al menos 3 caracteres' },
            maxLength: { value: 50, message: 'El nombre no puede tener mas de 50 caracteres' },
        },
        email: {
            required: 'El email es requerido',
            pattern: {
                value: /^[A-z0-9._%+-]+@[A-z0-9.-]+\.[A-z]{2,4}$/,
                message: 'El email no es valido',
            },
        },
        phone: {
            required: 'El telefono es requerido',
            pattern: { value: /^[0-9]+$/, message: 'El telefono no es valido' },
        },
    };

    const onSubmit = async (formData) => {
        try {
            setIsSubmitting(true);
            setShowAlert(false);

            await createLead({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                notes: formData.message || '',
                source: 'contact_form',
            });

            setAlertVariant('success');
            setAlertMessage('Su mensaje ha sido enviado correctamente. Nos comunicaremos con usted pronto.');
            setShowAlert(true);
            setIsSubmitted(true);
        } catch (err) {
            setAlertVariant('danger');
            setAlertMessage(getErrorMessage(err));
            setShowAlert(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onError = () => {
        setAlertVariant('danger');
        setAlertMessage('Debe completar todos los campos requeridos.');
        setShowAlert(true);
    };

    return (
        <Container className="my-5">
            <Helmet>
                <title>Contactenos - {data.title}</title>
                <meta name="description" content={`Contacte a ${data.title}. ${data.address}`} />
                <meta name="keywords" content="contacto, paomakeup, beauty studio, santo domingo" />
                <meta property="og:title" content={`Contactenos - ${data.title}`} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="/contactenos" />
                <meta property="og:site_name" content="PaoMakeup Beauty Studio" />
                <meta property="og:description" content={`Contacte a ${data.title}`} />
            </Helmet>

            <Row className="mb-3 mb-sm-5">
                <h1>Contactenos</h1>
            </Row>

            <Row>
                <Col sm={8}>
                    {showAlert && (
                        <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
                            {alertMessage}
                        </Alert>
                    )}
                    {isSubmitted ? (
                        <Container className="text-center mt-sm-5 animate__animated animate__fadeInUp">
                            <h1 className="display-4">Gracias por contactarnos.</h1>
                            <p className="display-6">En breve nos comunicaremos con usted.</p>
                        </Container>
                    ) : (
                        <Form className="text-start schedule-form contact-form px-sm-5" onSubmit={handleSubmit(onSubmit, onError)}>
                            <Row className="mb-3">
                                <Form.Group as={Col} sm={6} controlId="contactName">
                                    <Form.Label as="legend">Nombre completo</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ingrese su nombre"
                                        {...register('name', registerOptions.name)}
                                    />
                                    <small className="text-danger">
                                        {errors?.name && errors.name.message}
                                    </small>
                                </Form.Group>
                                <Form.Group as={Col} sm={6} controlId="contactEmail">
                                    <Form.Label as="legend">Correo electronico</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="Ingrese su correo"
                                        {...register('email', registerOptions.email)}
                                    />
                                    <small className="text-danger">
                                        {errors?.email && errors.email.message}
                                    </small>
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} sm={6} controlId="contactPhone">
                                    <Form.Label as="legend">Telefono</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        placeholder="Ingrese su telefono"
                                        {...register('phone', registerOptions.phone)}
                                    />
                                    <small className="text-danger">
                                        {errors?.phone && errors.phone.message}
                                    </small>
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="contactMessage">
                                    <Form.Label as="legend">Mensaje</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        placeholder="Escriba su mensaje aqui..."
                                        {...register('message')}
                                    />
                                </Form.Group>
                            </Row>
                            <Row className="mb-3 justify-content-center">
                                <Form.Group as={Row} sm={10} controlId="contactSubmit">
                                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <span className="d-inline-flex align-items-center gap-2">
                                                <Spinner animation="border" size="sm" /> Enviando...
                                            </span>
                                        ) : 'Enviar mensaje'}
                                    </Button>
                                </Form.Group>
                            </Row>
                        </Form>
                    )}
                </Col>
                <Col sm={4}>
                    <img className="w-50 mb-5" src="../assets/paomakeup/images/contact-us.jpg" alt="Contactenos Icon" />
                    <Row className="justify-content-center text-center">
                        <Col sm={8}>
                            <ul className="text-md-start list-unstyled custom-list">
                                <li>
                                    <FontAwesomeIcon className="fa-2x" icon={faPhone} />
                                    <span className="mx-4">{data.phone}</span>
                                </li>
                                <li>
                                    <FontAwesomeIcon className="fa-2x" icon={faWhatsapp} />
                                    <span className="mx-4">{data.whatsapp}</span>
                                </li>
                                <li>
                                    <FontAwesomeIcon className="fa-2x" icon={faEnvelope} />
                                    <span className="mx-4">{data.email}</span>
                                </li>
                                <li>
                                    <FontAwesomeIcon className="fa-2x" icon={faMapMarkerAlt} />
                                    <span className="mx-4" style={{ whiteSpace: 'pre-line' }}>{data.address}</span>
                                </li>
                            </ul>
                        </Col>
                    </Row>
                    <Row className="justify-content-center text-center">
                        <a href="/agendar-cita" className="btn btn-primary w-50 mx-3 my-2 text-white">Agendar cita</a>
                        <a href="/evaluacion-para-maquillaje-semipermanente" className="btn btn-primary w-50 mx-3 my-2 text-white">Evaluate gratis</a>
                    </Row>
                </Col>
            </Row>
        </Container>
    );
};

export default ContactUs;
